import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

// Les calculateurs déterministes du chantier 4, exposés au chat par dossier
// (chantier 9, décision D1).
//
// Ce que ce serveur ne fait pas, et qui est sa raison d'être : il ne calcule
// rien de neuf. Chaque outil appelle un calculateur existant, testé, qui somme
// en Postgres sur des centimes entiers [FR1]. L'agent ne fait donc AUCUNE
// arithmétique [FR2] — il appelle, il lit, il cite.
//
// Deux règles héritées du chantier 8 gouvernent les sorties :
//
//  - les montants sortent PRÉ-FORMATÉS (« −9 280,00 € »), jamais en centimes.
//    Demander à un modèle de diviser par cent sur chaque montant d'un dossier,
//    c'est déléguer une arithmétique au seul composant qui ne doit jamais
//    calculer. La seule façon fiable de l'en empêcher est de ne lui donner aucun
//    centime ;
//  - la lignée voyage avec le chiffre [FR4] : chaque sortie porte le dossier et
//    l'exercice utilisés, par IDENTIFIANT STABLE et pas seulement par libellé
//    (`cdo_…`, `fyr_…`), les numéros de comptes venant des résultats eux-mêmes.
//    Un nom de dossier se renomme et deux exercices peuvent commencer le même
//    jour ; un montant cité dans le chat doit rester forable jusqu'à l'écriture
//    six mois plus tard, pas seulement le jour où il a été dit.
//
// Et le périmètre n'est jamais un argument : aucun outil ne prend de dossier. Il
// est déduit de la conversation, donc hors de portée d'une injection de prompt.

export const COMPTA_CALCULATORS_SERVER_NAME = "compta_calculators" as const;

export const COMPTA_TOOL_REGULARITE = "regularite_en_la_forme";
export const COMPTA_TOOL_BALANCE = "balance_generale";
export const COMPTA_TOOL_BALANCE_TIERS = "balance_tiers";
export const COMPTA_TOOL_LETTRAGE = "lettrage";
export const COMPTA_TOOL_ACTIVITE = "activite_mensuelle";
export const COMPTA_TOOL_TVA = "coherence_tva";
export const COMPTA_TOOL_CUTOFF = "separation_exercices";
export const COMPTA_TOOL_GRAND_LIVRE = "grand_livre";
export const COMPTA_TOOL_OPTION_IR = "option_ir";

// Les descriptions sont à l'infinitif et disent CE QUE L'OUTIL REND, pas comment
// il le fait : c'est ce que le modèle lit pour choisir.
const asOfDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .describe(
    "Arrêté intermédiaire (AAAA-MM-JJ). Par défaut, la clôture de l'exercice."
  );

// Un collectif de tiers commence à deux chiffres (40, 41) ; une classe de charges
// tient sur un seul (6). Deux schémas, parce qu'une seule borne mentait à l'un ou
// à l'autre : la description d'`activite_mensuelle` donnait « 6 » en exemple quand
// le schéma exigeait deux chiffres — le modèle aurait suivi la phrase, pris un
// refus de validation, et recommencé.
const racineTiers = z
  .string()
  .regex(/^\d{2,8}$/)
  .describe("Racine d'un collectif de tiers, 2 à 8 chiffres (401, 411).");

const racineComptes = z
  .string()
  .regex(/^\d{1,8}$/)
  .describe("Racine de comptes, 1 à 8 chiffres (6, 60, 606100).");

export const COMPTA_CALCULATORS_TOOLS_METADATA = [
  {
    name: COMPTA_TOOL_REGULARITE,
    description:
      "Rendre la régularité EN LA FORME du fichier comptable de l'exercice : journaux présents DANS LE GRAND LIVRE — un journal jamais passé n'apparaît nulle part, et le fichier ne peut pas le trahir, ruptures de numérotation par journal, écritures déséquilibrées, écritures sans pièce, écritures non validées, journaux dont la numérotation n'est pas comparable. À consulter AVANT toute conclusion sur les comptes : elle dit ce que le fichier permet, ou interdit, de conclure.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Lecture de la régularité en la forme",
      done: "Régularité en la forme lue",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: COMPTA_TOOL_OPTION_IR,
    description:
      "Rendre où se situe l'exercice dans son option à l'impôt sur le revenu. Deux dispositifs que rien ne distingue à l'œil : la SARL de famille est ILLIMITÉE et n'a donc aucune échéance, l'option temporaire dure CINQ exercices non renouvelables. Pour la seconde, rend le rang de l'exercice dans le terme, ce qui reste après lui, et l'exercice qui bascule à l'impôt sur les sociétés quand le dossier le porte déjà. Le rang est compté sur les exercices RÉELS du dossier, jamais sur des années civiles. Ne dit PAS si l'option était ouverte au dossier : les conditions d'éligibilité (activité, ancienneté, détention) ne sont pas dans le profil.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Lecture de l'option IR",
      done: "Option IR lue",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: COMPTA_TOOL_BALANCE,
    description:
      "Rendre la balance générale du dossier : pour chaque compte mouvementé, les totaux débit et crédit et le solde, à la clôture ou à une date d'arrêté.",
    schema: { asOfDate },
    stake: "never_ask",
    displayLabels: {
      running: "Lecture de la balance générale",
      done: "Balance générale lue",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: COMPTA_TOOL_BALANCE_TIERS,
    description:
      "Rendre la balance auxiliaire d'un collectif de tiers : le solde par tiers sous une racine de comptes (401 fournisseurs, 411 clients). Sert à passer d'un solde collectif au détail qui le compose.",
    schema: { accountPrefix: racineTiers, asOfDate },
    stake: "never_ask",
    displayLabels: {
      running: "Lecture de la balance tiers",
      done: "Balance tiers lue",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: COMPTA_TOOL_LETTRAGE,
    description:
      "Rendre l'état du lettrage : si le dossier porte un lettrage sous les racines demandées, ce qui reste ouvert par tiers, la date du plus vieil élément non lettré DE L'EXERCICE IMPORTÉ, et le montant en jeu. Attention : cette date ne peut pas être antérieure à l'ouverture de l'exercice, même si la pièce est plus ancienne — ce n'est donc pas l'antériorité réelle de la créance ou de la dette. Sert à justifier un solde de tiers, pas à conclure sur une prescription ou une dépréciation.",
    schema: {
      accountPrefixes: z
        .array(racineTiers)
        .optional()
        .describe("Racines examinées. Par défaut 401 et 411."),
      asOfDate,
    },
    stake: "never_ask",
    displayLabels: {
      running: "Lecture du lettrage",
      done: "Lettrage lu",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: COMPTA_TOOL_ACTIVITE,
    description:
      "Rendre l'activité mensuelle sous une ou plusieurs racines de comptes : les mouvements mois par mois sur l'exercice. Sert à repérer un mois muet ou une variation qui appelle une explication.",
    schema: {
      accountPrefixes: z
        .array(racineComptes)
        .min(1)
        .describe("Racines examinées (par exemple 6 pour les charges)."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Lecture de l'activité mensuelle",
      done: "Activité mensuelle lue",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: COMPTA_TOOL_TVA,
    description:
      "Rendre la cohérence de la TVA : le taux implicite de TVA collectée rapproché des taux légaux MÉTROPOLITAINS, et le ratio de TVA déductible sur les achats et charges externes. Rend une VRAISEMBLANCE, jamais une déclaration ni un constat établi. La sortie dit aussi ce qu'elle a pu regarder : sans compte de produit, aucun taux ne peut être calculé, et l'absence de constat ne vaut alors pas absence d'anomalie.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Lecture de la cohérence TVA",
      done: "Cohérence TVA lue",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: COMPTA_TOOL_CUTOFF,
    description:
      "Rendre la composition des comptes de rattachement : les comptes RÉELLEMENT trouvés sous les racines demandées, puis le solde et le nombre de lignes par compte et par tiers. Un résultat vide avec zéro compte trouvé ne veut pas dire « rattachement propre » : il veut dire que le dossier ne porte aucun compte sous ces racines — un cabinet qui rattache en 428 ou 438 n'a aucun 408. Ne rend PAS les écritures elles-mêmes — pour les voir, appeler « grand_livre » sur le compte concerné. Ne teste pas non plus la séparation des exercices, qui suppose d'examiner ce qui a été comptabilisé autour de la clôture : ceci en montre la trace comptable, pas le bien-fondé.",
    schema: {
      // `racineTiers` (2 chiffres minimum) et non `racineComptes` : le
      // calculateur du rattachement refuse une racine d'un seul chiffre. Un
      // schéma plus permissif que son moteur est le miroir du défaut corrigé
      // juste avant — cette fois c'est l'outil qui promettrait ce que le calcul
      // n'accepte pas, et le modèle prendrait un échec là où on l'a invité.
      accountPrefixes: z
        .array(racineTiers)
        .optional()
        .describe(
          "Racines examinées. Par défaut 408, 418, 486 et 487 — un cabinet qui " +
            "rattache en 428, 438, 448 ou 4686 doit les demander explicitement."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Lecture de la séparation des exercices",
      done: "Séparation des exercices lue",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: COMPTA_TOOL_GRAND_LIVRE,
    description:
      "Rendre les LIGNES d'un compte, page par page : date, journal, pièce, libellé, tiers (numéro auxiliaire et libellé, quand la ligne en porte un), débit, crédit, code de lettrage. C'est le dernier étage du forage — celui qui montre le détail derrière un solde. Une limite à dire au collaborateur plutôt qu'à combler par déduction : la contrepartie d'une ligne n'est PAS rendue, et le libellé n'en tient pas lieu — pour la voir, il faut ouvrir le compte de contrepartie.",
    schema: {
      accountNumber: z
        .string()
        .regex(/^\d{3,20}$/)
        .describe("Numéro de compte exact (par exemple 401000)."),
      auxNumber: z
        .string()
        .optional()
        .describe(
          "Numéro de tiers auxiliaire, pour ne voir que ses lignes (par exemple FDUPONT)."
        ),
      withoutThirdParty: z
        .boolean()
        .optional()
        .describe(
          "Ne voir que les lignes SANS compte auxiliaire — celles que la balance tiers regroupe sous « sans tiers » et qui, sur un collectif, sont un constat de révision à part entière. IGNORÉ si « auxNumber » est fourni : les deux ne se combinent pas."
        ),
      cursor: z
        .string()
        .optional()
        .describe("Curseur rendu par l'appel précédent, pour la page suivante."),
      limit: z
        .number()
        .int()
        .min(1)
        .max(200)
        .optional()
        .describe("Nombre de lignes par page (au plus 200, 100 par défaut)."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Lecture du grand livre",
      done: "Grand livre lu",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
] as const;

export const COMPTA_CALCULATORS_SERVER = {
  serverInfo: {
    name: COMPTA_CALCULATORS_SERVER_NAME,
    version: "1.0.0",
    description:
      "Lire les chiffres du dossier comptable de cette conversation : balance, tiers, lettrage, activité, TVA, rattachement, grand livre, régularité en la forme, option IR. Lecture seule.",
    authorization: null,
    icon: "ActionTableIcon",
    documentationUrl: null,
  },
  tools: COMPTA_CALCULATORS_TOOLS_METADATA,
} as const satisfies ServerMetadata;
